import { useCallback, useEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { focusRing } from "@/lib/format";

export type ToastTone = "success" | "error" | "info";

export interface ToastState {
  id: number;
  message: string;
  tone: ToastTone;
  actionLabel?: string;
  onAction?: () => void;
  /** Announce at once (role="alert") even when not an error, e.g. a lost-change conflict. */
  assertive?: boolean;
}

interface ShowToastInput {
  message: string;
  tone?: ToastTone;
  actionLabel?: string;
  onAction?: () => void;
  // Override the default auto-dismiss timeout (ms). 0 disables auto-dismiss.
  durationMs?: number;
  assertive?: boolean;
}

// Lightweight single-slot toast queue. Replaces inline plazaMessage/plazaError
// strings without pulling in a heavier toast library.
export function useComprasToast() {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimer();
    setToast(null);
  }, [clearTimer]);

  const show = useCallback(
    (input: ShowToastInput) => {
      clearTimer();
      idRef.current += 1;
      const tone = input.tone ?? "success";
      const defaultDuration = tone === "error" ? 0 : 2500;
      const duration = input.durationMs ?? defaultDuration;
      const id = idRef.current;
      setToast({
        id,
        message: input.message,
        tone,
        actionLabel: input.actionLabel,
        onAction: input.onAction,
        assertive: input.assertive,
      });
      if (duration > 0) {
        timerRef.current = setTimeout(() => {
          setToast((current) => (current?.id === id ? null : current));
          timerRef.current = null;
        }, duration);
      }
    },
    [clearTimer],
  );

  useEffect(() => () => clearTimer(), [clearTimer]);

  return { toast, show, dismiss };
}

interface ComprasToastProps {
  toast: ToastState | null;
  onDismiss: () => void;
}

export function ComprasToast({ toast, onDismiss }: ComprasToastProps) {
  const visible = toast !== null;
  const tone = toast?.tone ?? "success";
  const urgent = tone === "error" || toast?.assertive === true;
  return (
    <div
      role={urgent ? "alert" : "status"}
      aria-live={urgent ? "assertive" : "polite"}
      // Every toast opens just below the app's sticky top bars: the navbar
      // (height published by app-navbar.tsx; 72px until measured) and, in
      // friend mode, the violet banner (friend-week-layout.tsx; 0 otherwise),
      // so it never covers the banner's save state.
      style={{
        top: "calc(env(safe-area-inset-top, 0px) + var(--app-navbar-height, 72px) + var(--friend-banner-height, 0px) + 12px)",
      }}
      className={cn(
        // Full width up to max-w-md (centred): a long message wraps across the
        // screen instead of collapsing into a narrow column; a short one stays
        // a compact pill in the middle.
        "pointer-events-none fixed inset-x-0 z-50 mx-auto flex w-full max-w-md justify-center px-4 motion-safe:transition-all motion-safe:duration-200 motion-safe:ease-out",
        visible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0",
      )}
    >
      {toast && (
        <div
          className={cn(
            "pointer-events-auto flex max-w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-[13px] font-medium shadow-md",
            tone === "success" &&
              "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100",
            tone === "error" &&
              "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100",
            tone === "info" && "border-border bg-card text-foreground",
          )}
        >
          <span className="min-w-0 flex-1">{toast.message}</span>
          {toast.actionLabel && toast.onAction && (
            <button
              type="button"
              onClick={() => {
                toast.onAction?.();
                onDismiss();
              }}
              className={cn(
                "shrink-0 rounded-md px-2 py-1 text-[12px] font-semibold underline-offset-2 transition-colors",
                tone === "error"
                  ? "text-red-900 hover:bg-red-100 dark:text-red-100 dark:hover:bg-red-900/40"
                  : "text-emerald-900 hover:bg-emerald-100 dark:text-emerald-100 dark:hover:bg-emerald-900/40",
                focusRing,
              )}
            >
              {toast.actionLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Cerrar"
            className={cn(
              "grid size-9 shrink-0 place-items-center rounded-md text-current opacity-60 transition-opacity hover:opacity-100",
              focusRing,
            )}
          >
            <XIcon className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
